import { memo, useEffect, useState } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react'
import { portOf, type WorkflowDef } from './workflowModel'
import './workflow.css'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The workflow card (Huanrui, 2026-10-16; workflowModel.ts says how it works)
 * -------------------------------------------------------------
 * One named port on the left for each input that changes, in its order;
 * the results out on the right. Run, Open (its steps, on their own page; a
 * double-click does the same), and the toolbar: add it to your own, bring
 * your toolbar's copy up to date with this one, or take it off.
 */
export interface WorkflowNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  glow: string
  open?: boolean
  workflow: WorkflowDef
  /** The page its steps are on. */
  wfPage: string
  running?: boolean
  progress?: string
  error?: string

  /* handed in by the board, never stored */
  /** Each changing input: its name, and how many pictures are wired into it. */
  ports?: { id: string; name: string; count: number }[]
  /** Its toolbar copy exists. */
  inToolbar?: boolean
  onEdit?: (patch: Partial<WorkflowNodeData>) => void
  onRun?: () => void
  onOpen?: () => void
  onToolbar?: (how: 'add' | 'update' | 'remove') => void
}

export const WorkflowNode = memo(function WorkflowNode({ id, data, selected }: NodeProps) {
  const card = data as WorkflowNodeData
  const def = card.workflow
  const ports = card.ports ?? []
  const running = card.running === true
  const isOpen = card.open !== false
  const remeasure = useUpdateNodeInternals()
  const [menu, setMenu] = useState(false)
  const portKey = ports.map((one) => one.id).join('|')
  useEffect(() => remeasure(id), [id, portKey, isOpen, remeasure])

  const counts = ports.map((one) => one.count)
  const runs = counts.length === 0 ? 1 : Math.max(...counts)
  const uneven = counts.some((n) => n !== 1 && n !== runs)
  const missing = ports.some((one) => one.count === 0)
  const why = missing
    ? `Wire a picture into ${ports.find((one) => one.count === 0)?.name ?? 'each input'}`
    : uneven
      ? 'Give each input the same number of pictures, or one'
      : ''
  const price =
    def.estimate === null ? 'credits vary' : def.estimate === 0 ? 'free' : `≈ ${def.estimate * runs} credits`

  return (
    <div
      className={[
        'cnode',
        'pnode',
        'wfnode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        ports.some((one) => one.count > 0) ? 'cnode--lit' : '',
        running ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={def.glyph || card.glyph} />
        </span>
        <span className="cnode__name" title={def.name}>
          {def.name}
        </span>
        <span className="wfnode__more nodrag">
          <button
            type="button"
            className="wfnode__dots"
            aria-label="Workflow menu"
            title="Toolbar"
            onClick={() => setMenu((open) => !open)}
          >
            ⋯
          </button>
          {menu ? (
            <span className="wfnode__menu" role="menu" onMouseLeave={() => setMenu(false)}>
              {card.inToolbar ? (
                <>
                  <button type="button" role="menuitem" onClick={() => (setMenu(false), card.onToolbar?.('update'))}>
                    Update in my toolbar
                  </button>
                  <button type="button" role="menuitem" onClick={() => (setMenu(false), card.onToolbar?.('remove'))}>
                    Remove from my toolbar
                  </button>
                </>
              ) : (
                <button type="button" role="menuitem" onClick={() => (setMenu(false), card.onToolbar?.('add'))}>
                  Add to my toolbar
                </button>
              )}
            </span>
          ) : null}
        </span>
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => card.onEdit?.({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {/* One row a changing input, its port beside it. */}
      <div className="wfnode__ports">
        {ports.length === 0 ? <p className="wfnode__none">Every input is fixed: Run makes it again.</p> : null}
        {ports.map((one) => (
          <div key={one.id} className="wfnode__port">
            <Handle type="target" id={portOf(one.id)} position={Position.Left} className="cnode__port wfnode__handle" />
            <span className="wfnode__port-name">{one.name}</span>
            <span className="wfnode__port-count">{one.count === 0 ? '—' : one.count}</span>
          </div>
        ))}
      </div>

      {isOpen ? (
        <div className="pnode__body">
          <p className="gnode__summary">
            {def.steps} step{def.steps === 1 ? '' : 's'} · {runs > 1 ? `${runs} runs · ` : ''}
            {price}
          </p>
          {card.progress ? <p className="gnode__summary">{card.progress}</p> : null}
          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {isOpen || running ? (
        <div className="gnode__actions">
          <button type="button" className="gnode__adjust nodrag" onClick={() => card.onOpen?.()} title="See and change its steps">
            Open
          </button>
          <button
            type="button"
            className="cnode__run pnode__run nodrag"
            onClick={() => card.onRun?.()}
            disabled={Boolean(why) || running}
            title={why || (runs > 1 ? `Run it ${runs} times, one for each set of pictures` : 'Run it on the pictures wired in')}
          >
            {running ? 'Running…' : runs > 1 ? `Run ${runs}` : 'Run'}
          </button>
        </div>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
